import Cookies, { type CookieAttributes } from "js-cookie";

export const USER_DATA_COOKIE_NAME = "userData";

/** Cookie key for quick verification check (value: "true" | "false"). */
export const IS_USER_VERIFIED_COOKIE_NAME = "isUserVerified";

/** 30 days - used as `expires` (days) for js-cookie */
const USER_DATA_COOKIE_EXPIRES_DAYS = 30;

/**
 * Shape of user data stored in the userData cookie.
 * Used for login state, lead checks, verification, course redirection, and form restoration.
 */
export interface UserData {
  name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isVerified: boolean;
  /** Mirror of isVerified for explicit verification checks (stored in cookie). */
  isUserVerified?: boolean;
  leads_id?: string;
  area_of_interest?: string;
  interest?: string;
  submittedInterestName?: string;
  highest_qualification?: string;
  /** Submitted program slug (program_name from programs API); used for same-course thank you check. */
  program_name?: string;
  /** POK for the submitted program; used for application redirect. */
  pok?: string;
  /**
   * Full post-login application path from submit API, e.g. `/application/{pok}?locale=in`.
   * Persisted so thank-you countdown / CTA work when returning to the site.
   */
  applicationRedirectUrl?: string;
  /** Persisted form fields for lead drop (phone, name, email, qualification, interest, pok, media, campaign, etc.) */
  formValues?: Record<string, unknown>;
  [key: string]: unknown;
}

const defaultCookieOptions: CookieAttributes = {
  path: "/",
  expires: USER_DATA_COOKIE_EXPIRES_DAYS,
  sameSite: "lax",
  secure: typeof window !== "undefined" && window.location?.protocol === "https:",
};

/**
 * Reads and parses the userData cookie.
 * When formValues is present, top-level name/email/phone/interest etc. are derived from formValues so readers get a single view.
 * Returns null if cookie is missing or corrupted (invalid JSON).
 */
export function getUserData(): UserData | null {
  if (typeof window === "undefined") return null;
  try {
    const value = Cookies.get(USER_DATA_COOKIE_NAME);
    if (!value) return null;
    const parsed = JSON.parse(value) as UserData;
    if (parsed && typeof parsed.isVerified !== "boolean") {
      parsed.isVerified = parsed.isVerified === true || String(parsed.isVerified) === "true";
    }
    parsed.isUserVerified = parsed.isVerified;
    if (parsed.formValues && typeof parsed.formValues === "object") {
      const fv = parsed.formValues as Record<string, unknown>;
      if (parsed.name === undefined)
        parsed.name = typeof fv["name"] === "string" ? (fv["name"] as string) : undefined;
      if (parsed.email === undefined)
        parsed.email = typeof fv["email"] === "string" ? (fv["email"] as string) : undefined;
      if (parsed.phone === undefined)
        parsed.phone = typeof fv["phone"] === "string" ? (fv["phone"] as string) : undefined;
      if (parsed.mobile === undefined)
        parsed.mobile =
          typeof (fv["mobile"] ?? fv["phone"]) === "string"
            ? ((fv["mobile"] ?? fv["phone"]) as string)
            : undefined;
      if (parsed.area_of_interest === undefined)
        parsed.area_of_interest =
          typeof fv["interest"] === "string" ? (fv["interest"] as string) : undefined;
      if (parsed.interest === undefined)
        parsed.interest =
          typeof fv["interest"] === "string" ? (fv["interest"] as string) : undefined;
      if (parsed.submittedInterestName === undefined)
        parsed.submittedInterestName =
          typeof fv["submittedInterestName"] === "string"
            ? (fv["submittedInterestName"] as string)
            : undefined;
      if (parsed.highest_qualification === undefined)
        parsed.highest_qualification =
          typeof (fv["qualification"] ?? fv["highest_qualification"]) === "string"
            ? ((fv["qualification"] ?? fv["highest_qualification"]) as string)
            : undefined;
      if (parsed.pok === undefined)
        parsed.pok = typeof fv["pok"] === "string" ? (fv["pok"] as string) : undefined;
      if (parsed.applicationRedirectUrl === undefined) {
        const u = fv["applicationRedirectUrl"];
        parsed.applicationRedirectUrl =
          typeof u === "string" ? u : undefined;
      }
    }
    return parsed;
  } catch {
    removeUserData();
    return null;
  }
}

/** Keys that represent the selected area of interest (course); always persisted together. */
export const AREA_OF_INTEREST_KEYS = [
  "area_of_interest",
  "interest",
  "submittedInterestName",
] as const;

/**
 * Keys that exist in formValues; when formValues is present, store these only inside formValues (no duplicate at top level).
 */
const FORM_VALUE_KEYS = [
  "name",
  "email",
  "phone",
  "mobile",
  "area_of_interest",
  "interest",
  "submittedInterestName",
  "highest_qualification",
  "qualification",
  "pok",
] as const;

/**
 * Stores the complete userData object in a cookie.
 * Merges with existing userData so area-of-interest keys and other fields are preserved when not provided.
 * Skips writing when the resulting data is unchanged (avoids duplicate saves).
 * Expiration: 30 days. Path: /. Secure on HTTPS.
 */
export function setUserData(data: UserData): void {
  if (typeof window === "undefined") return;
  try {
    const existing = getUserData();
    const merged: UserData = {
      ...(existing || { isVerified: false }),
      ...data,
      isVerified: data.isVerified === true || String(data.isVerified) === "true",
      isUserVerified: data.isVerified === true || String(data.isVerified) === "true",
    };
    for (const key of AREA_OF_INTEREST_KEYS) {
      if (merged[key] === undefined && existing?.[key] !== undefined) {
        merged[key] = existing[key];
      }
    }
    if (data.formValues && typeof data.formValues === "object") {
      for (const key of FORM_VALUE_KEYS) {
        delete merged[key];
      }
    }
    const value = JSON.stringify(merged);
    const currentValue = Cookies.get(USER_DATA_COOKIE_NAME);
    if (currentValue === value) return;
    Cookies.set(USER_DATA_COOKIE_NAME, value, defaultCookieOptions);
    Cookies.set(
      IS_USER_VERIFIED_COOKIE_NAME,
      merged.isVerified ? "true" : "false",
      defaultCookieOptions,
    );
  } catch (e) {
    console.error("Error setting userData cookie:", e);
  }
}

export function removeUserData(): void {
  if (typeof window === "undefined") return;
  Cookies.remove(USER_DATA_COOKIE_NAME, { path: "/" });
  Cookies.remove(IS_USER_VERIFIED_COOKIE_NAME, { path: "/" });
  try {
    sessionStorage.removeItem("leadApplicationRedirectUrl");
  } catch {
    /* ignore */
  }
}

export function getFormValuesFromCookie(): Record<string, unknown> | null {
  const data = getUserData();
  const fv = data?.formValues;
  return fv && typeof fv === "object" ? fv : null;
}

export function setFormValuesInCookie(formValues: Record<string, unknown>): void {
  const existing = getUserData();
  const existingFormValues = existing?.formValues;
  if (
    existingFormValues &&
    typeof existingFormValues === "object" &&
    JSON.stringify(existingFormValues) === JSON.stringify(formValues)
  ) {
    return;
  }
  setUserData({
    ...(existing || { isVerified: false }),
    formValues,
  });
}

export function isUserLoggedIn(): boolean {
  const data = getUserData();
  return data !== null && data.isVerified === true;
}

export function isUserVerified(): boolean {
  if (typeof window === "undefined") return false;
  const value = Cookies.get(IS_USER_VERIFIED_COOKIE_NAME);
  if (value !== undefined) return value === "true";
  const data = getUserData();
  return data?.isVerified === true || data?.isUserVerified === true;
}

export function getSubmittedInterest(): string | null {
  const data = getUserData();
  const value = data?.area_of_interest || data?.interest;
  return value && typeof value === "string" ? value : null;
}

/** Normalize to comparable course key (prefer erp_unicode when list matches). */
export function normalizeLeadCourseInterestKey(
  raw: string | null | undefined,
  courses?: Array<{ erp_unicode?: string | null; id?: number | string | null }>,
): string {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (!courses || courses.length === 0) return s;
  const match = courses.find(
    (c) =>
      String(c.erp_unicode ?? "").trim() === s ||
      String(c.id ?? "") === s,
  );
  if (!match) return s;
  const erp = String(match.erp_unicode ?? "").trim();
  return erp || String(match.id ?? "");
}

/** Normalized cached submission keys from cookie (for multi-programme detection). */
export function getCachedLeadCourseInterestKeys(
  courses?: Array<{ erp_unicode?: string | null; id?: number | string | null }>,
): string[] {
  const keys = new Set<string>();
  const append = (raw: unknown) => {
    if (typeof raw !== "string" || !raw.trim()) return;
    const n = normalizeLeadCourseInterestKey(raw.trim(), courses);
    if (n) keys.add(n);
  };
  const data = getUserData();
  append(data?.area_of_interest);
  append(data?.interest);
  const fv = data?.formValues;
  if (fv && typeof fv === "object" && !Array.isArray(fv)) {
    const o = fv as Record<string, unknown>;
    append(o.course);
    append(o.interest);
    append(o.area_of_interest);
  }
  return [...keys];
}

export function getSubmittedInterestName(): string | null {
  const data = getUserData();
  const fv = data?.formValues;
  const fromForm =
    fv &&
    typeof fv === "object" &&
    typeof (fv as { submittedInterestName?: string }).submittedInterestName === "string"
      ? (fv as { submittedInterestName: string }).submittedInterestName
      : null;
  return (
    fromForm ??
    (data?.submittedInterestName && typeof data.submittedInterestName === "string"
      ? data.submittedInterestName
      : null)
  );
}

export function getSubmittedProgramSlug(): string | null {
  const data = getUserData();
  const value = data?.program_name;
  return value && typeof value === "string" ? value : null;
}

/** Application path from cookie (top-level, formValues, or legacy `pok` only). */
export function getStoredApplicationRedirectUrl(): string {
  if (typeof window === "undefined") return "";
  const data = getUserData();
  if (data) {
    const fromTop =
      typeof data.applicationRedirectUrl === "string"
        ? data.applicationRedirectUrl.trim()
        : "";
    if (fromTop) return fromTop;
    const fv = data.formValues;
    if (fv && typeof fv === "object" && !Array.isArray(fv)) {
      const r = (fv as Record<string, unknown>)["applicationRedirectUrl"];
      if (typeof r === "string" && r.trim()) return r.trim();
      const pokVal = (fv as Record<string, unknown>)["pok"];
      if (typeof pokVal === "string" && pokVal.trim()) {
        return `/application/${encodeURIComponent(pokVal.trim())}?locale=in`;
      }
    }
    const pok =
      typeof data.pok === "string" && data.pok.trim()
        ? data.pok.trim()
        : "";
    if (pok) return `/application/${encodeURIComponent(pok)}?locale=in`;
  }
  try {
    const s = sessionStorage.getItem("leadApplicationRedirectUrl");
    if (typeof s === "string" && s.trim()) return s.trim();
  } catch {
    /* ignore */
  }
  return "";
}

export function isSameCourseAsSubmitted(currentCourseId: string | null): boolean {
  if (!currentCourseId) return false;
  const submitted = getSubmittedInterest();
  return submitted !== null && String(submitted) === String(currentCourseId);
}

export function isSameCourseAsSubmittedBySlug(currentSlug: string | null): boolean {
  if (!currentSlug) return false;
  const submittedSlug = getSubmittedProgramSlug();
  return submittedSlug !== null && String(submittedSlug) === String(currentSlug);
}

export function shouldSkipSubmitAndShowThankYou(
  currentSlugOrCourseId: string | null,
  options?: { useSlugOnly?: boolean },
): boolean {
  const data = getUserData();
  if (!data || data.isVerified !== true) return false;
  const useSlugOnly = options?.useSlugOnly ?? true;
  if (useSlugOnly && data.program_name) {
    return isSameCourseAsSubmittedBySlug(currentSlugOrCourseId);
  }
  return isSameCourseAsSubmitted(currentSlugOrCourseId);
}
